/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useEmployeeDashboard.ts
 * PURPOSE: TanStack Query wrapper for Employee Dashboard — aggregates attendance, tasks, schedules.
 *          2026-08-12: + Query 7 (early-clock-in tolerance) — sync mobile PR #603
 *          (BS-300 §1bis-G5) so the Route 1 clock-in quick action can show the
 *          same non-blocking early-warning banner mobile ships.
 *          2026-08-29 (BOT-001 1.3a, GH#648): pending-task count now sourced
 *          SOLELY from GET /api/v2/tasks/my (mobile-anchored) instead of the
 *          legacy GET /api/v1/tasks/summary — matches the ratified data
 *          source in WIREFRAME-V0-MASTER-REGISTRY.md §2 SC-019-EMP-HOME
 *          ANNOTATION [5], and fixes an undercount bug (OVERDUE tasks were
 *          excluded from the pending filter; the query was also scoped to
 *          shift_date=today only, missing tasks overdue from prior days).
 *          2026-09-02 (#704 BOT-015): + hasDraftSchedule (Query 9) — GET
 *          /my-schedule intentionally hides DRAFT (unpublished) schedules
 *          from Employee, so a month with ONLY draft shifts looked identical
 *          to "never scheduled" (isFresh=true) and showed the misleading
 *          "Chưa được phân ca" message. isFresh now also requires
 *          !hasDraftSchedule; EmployeeDashboard.tsx renders a distinct
 *          "not published yet" message when hasDraftSchedule is true.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02
 * COMPLIANCE: SDLC 6.5.0 + BS-300-PHAN-DOAN-C-NHAN-VIEN-CHAM-CONG §1bis (mobile↔web parity)
 * PILLAR: P1 (Stage 04 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance, Rule 7: Performance SLAs (<50ms p95)
 *
 * @business-context
 * - Domain: Employee Self-Service, Dashboard Data Aggregation
 * - Impact: Employee Experience — real-time personal workspace
 * - Stakeholders: Employees (71), HR Team
 *
 * @technical-architecture
 * - Layer: Business Logic Layer, Custom Hooks
 * - Pattern: TanStack Query, Parallel Fetch, Cache Invalidation
 * - Dependencies: @tanstack/react-query v5, API Services (attendance, task, schedule)
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/SPEC-0407-manager-dashboards-design.md (FR-004, FR-005)
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md §2 SC-019-EMP-HOME (✅ CHỐT — pending-count source of truth)
 * - Requirements: docs/01-planning/01-requirements/06-dashboard/BR-DASH-002-employee-dashboard.md
 * - Decision: docs/02-design/18-adrs/06-decisions/DD-0026-employee-dashboard-realtime-offline-architecture.md
 * - Mobile parity source: mobile/src/screens/attendance/AttendanceScreen.tsx (PR #603); mobile/src/services/api/taskApi.ts (PENDING_TASK_STATUSES)
 * - Timing logic: frontend/src/utils/attendanceTiming.ts
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';
import { attendanceApi } from '../services/api/attendance';
import { Task } from '../services/api/task';
import { apiV2 } from '../services/api/config';
import { scheduleApi, Schedule } from '../services/api/schedule';
import { DASHBOARD_QUERY_KEYS } from './queryKeys';
import { DEFAULT_EARLY_CLOCKIN_TOLERANCE_MINUTES } from '../utils/attendanceTiming';
import { toLocalISODate } from '@/utils/date';

// ============================================================================
// Types — aligned with BR-DASH-002 §2
// ============================================================================

/** BR-DASH-002.1: Three attendance states */
export type AttendanceState = 'not_clocked_in' | 'working' | 'shift_ended';

/** Typed API response — replaces `as any` casts (Architecture Review MAJOR #3) */
export interface AttendanceStatusResponse {
  is_clocked_in: boolean;
  has_completed_shift: boolean;
  clock_in_time: string | null;
  late_count: number;
  on_time_rate: number;
  current_attendance_id?: string | null;
}

export interface EmployeeMonthlyStats {
  shiftsWorked: number;
  pendingTaskCount: number;
  lateCount: number;
  onTimeRate: number;
}

export interface EmployeeDashboardData {
  attendanceState: AttendanceState;
  attendanceId: string | null;
  clockInTime: string | null;
  clockInPhoto: string | null;
  todayShift: Schedule | null;
  upcomingShift: Schedule | null;
  pendingTasks: Task[];
  monthlyStats: EmployeeMonthlyStats;
  /** PR #603 sync (2026-08-12, BS-300 §1bis-G5) — tenant-configured early
   * clock-in tolerance in minutes; defaults to 30 until the query resolves. */
  earlyClockinToleranceMinutes: number;
  /** SC-019 gap G3/G7 (2026-08-25) — clock-out time of the completed shift
   * (DEFAULT-ENDED state renders "Vào {HH:mm} · Ra {HH:mm}"); null when the
   * backend does not report one. */
  clockOutTime: string | null;
  /** SC-019 gap G3 (2026-08-25) — FRESH state: employee has NEVER been
   * scheduled this month (month schedule window completely empty AND no
   * DRAFT schedule either — see hasDraftSchedule below). Mirrors mobile
   * HomeScreen.tsx:393-421 which detects FRESH from an empty month. */
  isFresh: boolean;
  /** #704 (BOT-015, 2026-09-02) — true when the month schedule window is
   * empty of PUBLISHED/CONFIRMED schedules but the employee DOES have a
   * DRAFT schedule (Manager is drafting, not yet published). Distinguishes
   * "never scheduled" (isFresh) from "scheduled, not published yet" so the
   * dashboard doesn't misleadingly say "Chưa được phân ca". */
  hasDraftSchedule: boolean;
}

// Re-export for backward compatibility
export { DASHBOARD_QUERY_KEYS } from './queryKeys';

// DD-0026 §2.2: SPEC-0407 §2.2 — 30-second fallback polling
const POLLING_INTERVAL = 30_000;
const STALE_TIME = 30_000;

// ============================================================================
// Hook
// ============================================================================

interface UseEmployeeDashboardOptions {
  /** When WS is connected, disable polling (Architecture Review MINOR #4) */
  isWsConnected?: boolean;
}

export function useEmployeeDashboard(options: UseEmployeeDashboardOptions = {}) {
  const { isWsConnected = false } = options;
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();
  const enabled = !!accessToken;

  // MINOR #4: Only poll when WebSocket is NOT connected (fallback mode)
  const refetchInterval = isWsConnected ? false as const : POLLING_INTERVAL;

  const today = toLocalISODate(new Date());
  const tomorrow = toLocalISODate(new Date(Date.now() + 86400000));

  // ── Query 1: Attendance Status (BR-DASH-002.1) ──
  const attendanceQuery = useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.attendanceStatus,
    queryFn: () => attendanceApi.attendance_status_get__includes_predictions(accessToken!),
    enabled,
    staleTime: STALE_TIME,
    // bot-137 (GH#1064): clock-in/out happens on /attendance, which does not
    // share this cache. Without 'always', returning here within STALE_TIME
    // ("Về trang chủ" after clock-out) re-rendered the cached in-shift state
    // with "Chấm công ra", so the button looked like it did nothing.
    refetchOnMount: 'always',
    refetchInterval,
  });

  // ── Query 2: Pending Tasks via V2 API (BR-DASH-002.4, ISS-016-D fix) ──
  // V2 ShiftTask statuses considered "pending": ASSIGNED, ACKNOWLEDGED,
  // IN_PROGRESS, OVERDUE — mirrors mobile PENDING_TASK_STATUSES exactly
  // (mobile/src/services/api/taskApi.ts:76-81). No shift_date filter: a task
  // overdue from a previous day must still count (fixed 2026-08-29, BOT-001
  // 1.3a — the old shift_date=today scope + missing OVERDUE both undercounted
  // vs mobile's getPendingTaskCount()).
  const tasksQuery = useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.pendingTasks,
    queryFn: async (): Promise<Task[]> => {
      const resp = await apiV2.get('/tasks/my', {
        params: { limit: 100 },
      });
      const items = resp.data?.items ?? resp.data ?? [];
      // Map V2 TaskResponse → V1 Task shape for dashboard compatibility
      return items
        .filter((t: any) => ['ASSIGNED', 'ACKNOWLEDGED', 'IN_PROGRESS', 'OVERDUE'].includes(t.status))
        .map((t: any) => ({
          id: t.id,
          title: t.title,
          description: t.description || '',
          priority: t.task_type === 'URGENT' ? 'urgent' : 'medium',
          status: t.status.toLowerCase(),
          due_date: t.deadline,
          category: 'other',
          created_by_id: 0,
          estimated_hours: 0,
          completion_percentage: 0,
        } as Task));
    },
    enabled,
    staleTime: STALE_TIME,
    refetchInterval,
  });

  // ── Query 3: Today's Schedule (BR-DASH-002.5 / SPEC-0407 §4.2) ──
  const todayScheduleQuery = useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.todaySchedule,
    queryFn: () => scheduleApi.getMySchedule(accessToken!, today, today),
    enabled,
    staleTime: STALE_TIME,
    refetchInterval,
  });

  // ── Query 4: Tomorrow's Schedule (BR-DASH-002.5) ──
  const tomorrowScheduleQuery = useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.tomorrowSchedule,
    queryFn: () => scheduleApi.getMySchedule(accessToken!, tomorrow, tomorrow),
    enabled,
    staleTime: STALE_TIME,
    refetchInterval,
  });

  // ── Query 5 removed 2026-08-29 (BOT-001 1.3a, GH#648) ──
  // Was GET /api/v1/tasks/summary via taskApi.getTaskSummary(), used only to
  // feed monthlyStats.pendingTaskCount below (v1-first, v2-length fallback).
  // Mobile has no v1 equivalent; the ratified SC-019-EMP-HOME spec names
  // GET /api/v2/tasks/my (Query 2 above) as the sole pending-count source —
  // removed as a dead/drifted fallback rather than kept. taskApi.getTaskSummary()
  // itself is NOT removed — still used by useTask.ts for the Manager task views
  // (out of scope for this fix).

  // ── Query 8: Month schedule (SC-019 gap G3 — FRESH detection) ──
  // FRESH = nhân viên mới, lịch THÁNG rỗng hoàn toàn, chưa từng được phân ca
  // (wireframe SC-019-EMP-HOME state FRESH; mobile HomeScreen.tsx:393-421).
  const monthStart = `${today.substring(0, 7)}-01`;
  const monthEndDate = new Date(
    Number(today.substring(0, 4)),
    Number(today.substring(5, 7)),
    0,
  )
    .toISOString()
    .split('T')[0];
  const monthScheduleQuery = useQuery({
    queryKey: [...DASHBOARD_QUERY_KEYS.todaySchedule, 'month', monthStart],
    queryFn: () => scheduleApi.getMySchedule(accessToken!, monthStart, monthEndDate),
    enabled,
    staleTime: STALE_TIME,
    refetchInterval,
  });

  // ── Query 9: Draft-schedule status (#704 BOT-015, 2026-09-02) ──
  // GET /my-schedule (Query 8 above) CỐ Ý ẩn Schedule.status=DRAFT khỏi
  // Employee — nên khi cả tháng chỉ có ca DRAFT, monthScheduleQuery trả []
  // giống hệt "chưa từng được phân ca". Chỉ fetch khi lịch tháng rỗng
  // (ứng viên FRESH) để tránh gọi thừa; trả về 1 boolean, KHÔNG lộ chi tiết
  // ca DRAFT (giữ nguyên nguyên tắc ẩn DRAFT của endpoint chính).
  const monthScheduleEmpty =
    monthScheduleQuery.isSuccess &&
    Array.isArray(monthScheduleQuery.data) &&
    monthScheduleQuery.data.length === 0;
  const draftStatusQuery = useQuery({
    queryKey: [...DASHBOARD_QUERY_KEYS.monthScheduleDraftStatus, monthStart],
    queryFn: () => scheduleApi.getMyScheduleDraftStatus(accessToken!, monthStart, monthEndDate),
    enabled: enabled && monthScheduleEmpty,
    staleTime: STALE_TIME,
    refetchInterval,
  });

  // ── Query 7: Early clock-in tolerance (BS-300 §1bis-G5, PR #603 sync) ──
  // Same endpoint mobile's useBranchLocations() reads (backend
  // gps_attendance.py get_branch_locations); the branches array itself isn't
  // needed here (route 1's clock action doesn't do per-branch geofence math),
  // only the tenant-configured `early_clockin_tolerance_minutes` field.
  const earlyToleranceQuery = useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.earlyClockinTolerance,
    queryFn: async (): Promise<number> => {
      const res = await fetch(`${window.location.origin}/api/v1/gps-attendance/branches/locations`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) return DEFAULT_EARLY_CLOCKIN_TOLERANCE_MINUTES;
      const data = await res.json();
      return typeof data?.early_clockin_tolerance_minutes === 'number'
        ? data.early_clockin_tolerance_minutes
        : DEFAULT_EARLY_CLOCKIN_TOLERANCE_MINUTES;
    },
    enabled,
    staleTime: STALE_TIME,
    refetchInterval,
  });

  // ── Query 6: Today's clock-in photo (fetched once, no polling) ──
  const hasPhoto = !!(attendanceQuery.data as any)?.has_clock_in_photo;
  const photoQuery = useQuery({
    queryKey: [...DASHBOARD_QUERY_KEYS.attendanceStatus, 'photo'],
    queryFn: async () => {
      const res = await fetch(
        `${window.location.origin}/api/v1/attendance/today-photo`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!res.ok) return null;
      const data = await res.json();
      return data.clock_in_photo || null;
    },
    enabled: enabled && hasPhoto,
    staleTime: Infinity, // Only fetch once per session
    refetchInterval: false,
  });

  // ── Derive attendance state (3-state: BR-DASH-002.1) ──
  // Backend returns: { status: "clocked_in"|"clocked_out"|"not_clocked_in", last_clock_in, ... }
  // Map to AttendanceStatusResponse fields for compatibility
  let attendanceState: AttendanceState = 'not_clocked_in';
  let clockInTime: string | null = null;
  const attnData = attendanceQuery.data as (AttendanceStatusResponse & { status?: string; last_clock_in?: string; clock_in_photo?: string }) | undefined;
  if (attnData) {
    // Support both formats: { is_clocked_in: bool } and { status: "clocked_in" }
    const isClockedIn = attnData.is_clocked_in || attnData.status === 'clocked_in';
    const hasCompleted = attnData.has_completed_shift || attnData.status === 'clocked_out';
    if (isClockedIn) {
      attendanceState = 'working';
      clockInTime = attnData.clock_in_time || attnData.last_clock_in || null;
    } else if (hasCompleted) {
      attendanceState = 'shift_ended';
      // 2026-08-25 (verify SUMMARY mục 2): ca đã kết thúc vẫn phải map
      // clock_in_time, nếu không DEFAULT-ENDED render "Vào --:--".
      clockInTime = attnData.clock_in_time || attnData.last_clock_in || null;
    }
  }

  // ── Derive tasks ──
  const pendingTasks: Task[] = Array.isArray(tasksQuery.data) ? tasksQuery.data : [];

  // ── Derive schedules ──
  // #1527 (BOT-153): GET /schedules/my-schedule returns CANCELLED rows on
  // purpose (the week list renders "Nghỉ (ca đã huỷ)"), so a shift the
  // Manager deleted and re-published must be skipped here, not taken as [0].
  const firstLiveShift = (rows: unknown): Schedule | null =>
    Array.isArray(rows) ? rows.find((s: Schedule) => s.status !== 'cancelled') ?? null : null;
  const todayShift = firstLiveShift(todayScheduleQuery.data);
  const upcomingShift = firstLiveShift(tomorrowScheduleQuery.data);

  // ── Derive monthly stats (BR-DASH-002.2) ──
  // pendingTaskCount = pendingTasks.length (Query 2, GET /v2/tasks/my) — sole
  // source since 2026-08-29 (BOT-001 1.3a); see Query 2/Query 5 notes above.
  const monthlyStats: EmployeeMonthlyStats = {
    shiftsWorked: todayShift ? 1 : 0,
    pendingTaskCount: pendingTasks.length,
    lateCount: attnData?.late_count ?? 0,
    onTimeRate: attnData?.on_time_rate ?? 0,
  };

  // ── Aggregate state ──
  const isLoading =
    attendanceQuery.isLoading || tasksQuery.isLoading || todayScheduleQuery.isLoading;
  const isError =
    attendanceQuery.isError && tasksQuery.isError && todayScheduleQuery.isError;
  const error = attendanceQuery.error || tasksQuery.error || todayScheduleQuery.error;

  // ── Refetch all (for pull-to-refresh + WebSocket invalidation) ──
  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all });
  };

  const clockInPhoto: string | null = (photoQuery.data as string) || null;

  const attendanceId: string | null = attnData?.current_attendance_id
    ?? (attnData as any)?.attendance_id
    ?? null;

  const earlyClockinToleranceMinutes: number =
    typeof earlyToleranceQuery.data === 'number' ? earlyToleranceQuery.data : DEFAULT_EARLY_CLOCKIN_TOLERANCE_MINUTES;

  // SC-019 gap G7 — clock-out of the completed shift (DEFAULT-ENDED).
  const clockOutTime: string | null =
    (attnData as any)?.clock_out_time ?? (attnData as any)?.last_clock_out ?? null;

  // #704 (BOT-015) — has_draft_schedule chỉ có ý nghĩa khi monthScheduleEmpty
  // (draftStatusQuery chỉ fetch trong điều kiện đó); mặc định false.
  const hasDraftSchedule = !!draftStatusQuery.data?.has_draft_schedule;

  // SC-019 gap G3 — FRESH = month schedule window completely empty AND
  // no DRAFT schedule pending publish either (#704 BOT-015 refinement:
  // a DRAFT-only month is NOT fresh, just "not published yet").
  const monthSchedules: Schedule[] = Array.isArray(monthScheduleQuery.data)
    ? monthScheduleQuery.data
    : [];
  const isFresh =
    !isLoading &&
    monthScheduleQuery.isSuccess &&
    monthSchedules.length === 0 &&
    !hasDraftSchedule;

  const data: EmployeeDashboardData = {
    attendanceState,
    attendanceId,
    clockInTime,
    clockInPhoto,
    todayShift,
    upcomingShift,
    pendingTasks,
    monthlyStats,
    earlyClockinToleranceMinutes,
    clockOutTime,
    isFresh,
    hasDraftSchedule,
  };

  return {
    data,
    isLoading,
    isError,
    error,
    refetchAll,
    queryClient,
  };
}
